import { normalise } from "$root/functional-areas/instruments-editor/builder";
import type { UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { labSub, labSum, weightPrecisionForAum } from "../../../utils/math";
import BigNumber from "bignumber.js";
import { OrderedMap, type Map as ImmutableMap } from "immutable";
import type { PortfolioLabComposition } from "../../../ctx";
import { classOf } from "../classify";
import { distributeWeightsToTarget, reconcileMonetaryWeights } from "./aum";
import { rescalePortfolioOuterWeight, scaleAllocationScope } from "./editor";

const NORMALIZE_PRECISION = 2;

export function createNormalizeEditor({
	composition,
	instrumentPool,
	groupBy,
	aum,
}: {
	composition: PortfolioLabComposition[];
	instrumentPool: ImmutableMap<string, UserInstrumentDto>;
	groupBy: string;
	aum: number | null;
}) {
	// Derived once and threaded into the shared editor rescale paths (rescalePortfolioOuterWeight,
	// scaleAllocationScope) so their inner weights land on the AUM grid — matching the weight-edit API.
	const weightPrecision = weightPrecisionForAum(aum);
	return {
		// Rescale selected top-level items (portfolio UUIDs + direct instrument tickers) so all
		// top-level weights sum to 100. Used in HOLDINGS mode.
		normalizeTopLevel(selectedIds: string[]): PortfolioLabComposition[] {
			const topLevelMap = OrderedMap<string, BigNumber>(
				composition.map((c) => [c.type === "instrument" ? c.tickerName : c.uuid, BigNumber(c.weight)]),
			);
			const normalized = normalise({
				distributeTo: selectedIds,
				allocation: topLevelMap,
				decimalPlaces: NORMALIZE_PRECISION,
			});
			const updated = composition.map((c) => {
				const id = c.type === "instrument" ? c.tickerName : c.uuid;
				const newWeight = normalized.get(id);
				if (newWeight == null) {
					return c;
				}
				if (c.type === "portfolio") {
					// Inner weights are effective: they must scale with the outer weight.
					return rescalePortfolioOuterWeight(c, newWeight.toNumber(), aum, weightPrecision);
				}
				return {
					...c,
					weight: newWeight.toNumber(),
				};
			});
			return reconcileMonetaryWeights(updated, aum);
		},

		// Scale effective weights of all instruments in selected groups so the total composition sums to 100.
		// Unselected items keep their weights; selected groups absorb the gap. Used in ALLOCATION mode.
		normalizeGroups(selectedLabels: string[]): PortfolioLabComposition[] {
			const inSelectedGroup = (ticker: string) => selectedLabels.includes(classOf(ticker, instrumentPool, groupBy));
			const unselectedWeight = labSum(composition, (c) => {
				if (c.type === "instrument") {
					return inSelectedGroup(c.tickerName) ? 0 : c.weight;
				}
				return labSum(
					c.instruments.filter((i) => !inSelectedGroup(i.tickerName)),
					(i) => i.weight,
				);
			});
			// scaleAllocationScope already reconciles monetary; reconciling again is idempotent.
			return reconcileMonetaryWeights(
				scaleAllocationScope(composition, inSelectedGroup, labSub(100, unselectedWeight), aum, weightPrecision),
				aum,
			);
		},

		// Rescale only the matching inner instruments of one portfolio so the portfolio is internally
		// consistent again: matching weights absorb the gap between the outer weight and the
		// non-matching inner sum (inner weights are effective, so sum(inner) === outer must hold).
		// Does not change the portfolio's outer weight. Used in ALLOCATION mode for portfolio-slice rows.
		normalizeGroupSlice(portfolioUuid: string, allocationLabel: string): PortfolioLabComposition[] {
			const updated = composition.map((c) => {
				if (c.type !== "portfolio" || c.uuid !== portfolioUuid) {
					return c;
				}
				const inGroup = (ticker: string) => classOf(ticker, instrumentPool, groupBy) === allocationLabel;
				const matching = c.instruments.filter((i) => inGroup(i.tickerName));
				if (matching.length === 0) {
					return c;
				}
				const nonMatchingSum = labSum(
					c.instruments.filter((i) => !inGroup(i.tickerName)),
					(i) => i.weight,
				);
				const targetMatchingSum = Math.max(0, labSub(c.weight, nonMatchingSum));
				// Distribute the target across the matching instruments onto the AUM grid with an EXACT
				// sum (largest-remainder), so sum(inner) === outer holds with no rounding tail. A single
				// scale factor with each product independently rounded drifted the sum off the target
				// (e.g. 39.99999999999999, read as a stuck 99.99 % by the truncating totals cell) — the
				// same tail MDOT-24563 removed from the other normalize paths. distributeWeightsToTarget
				// also handles the all-zero bootstrap (→ equal share) internally.
				const matchingWeights = distributeWeightsToTarget(
					matching.map((i) => i.weight),
					targetMatchingSum,
					weightPrecision,
				);
				let matchingIdx = 0;
				const updatedInstruments = c.instruments.map((i) =>
					inGroup(i.tickerName) ? { ...i, weight: matchingWeights[matchingIdx++] } : i,
				);
				return { ...c, instruments: updatedInstruments };
			});
			return reconcileMonetaryWeights(updated, aum);
		},

		// Scale selected flat instruments (by effective weight) to sum to 100, then back-propagate
		// portfolio outer weights (outer === sum of inner effective weights). Used in UNGROUP mode.
		//
		// Delegates to the shared `normalise` — the same exact-sum algorithm the top-level path uses.
		// It distributes the 100 % gap across ONLY the selected leaves and drives the remainder to
		// exactly 0 on the weight grid. A single proportional scale factor (the previous approach)
		// rounded each product independently at 14 dp, leaving a rounding tail that drifted the total
		// to e.g. 99.9999999999999 — read as a stuck 99.99 % by the truncating totals cell.
		//
		// `weightTotalPrecision` is the customer precision the totals rule rounds at. `normalise` floors
		// its residual, so it can leave up to one step over 100 — or call an excess below one step
		// nothing to do. Running one place finer than that precision keeps the residual under the
		// half-step the rule rounds away; the weight grid wins when it is already finer.
		normalizeFlatInstruments(
			selected: Array<{ tickerName: string; portfolioUuid?: string }>,
			weightTotalPrecision: number,
		): PortfolioLabComposition[] {
			// Unique key per leaf so a direct holding never collides with an identically-named
			// instrument nested inside a portfolio.
			const directKey = (ticker: string) => `d:${ticker}`;
			const nestedKey = (portfolioUuid: string, ticker: string) => `p:${portfolioUuid}:${ticker}`;

			// Flat allocation over every leaf (effective weights), in composition order.
			const allocation = OrderedMap<string, BigNumber>(
				composition.flatMap(
					(c): Array<[string, BigNumber]> =>
						c.type === "instrument"
							? [[directKey(c.tickerName), BigNumber(c.weight)]]
							: c.instruments.map((i) => [nestedKey(c.uuid, i.tickerName), BigNumber(i.weight)]),
				),
			);

			const selectedKeys = selected.map((s) =>
				s.portfolioUuid == null ? directKey(s.tickerName) : nestedKey(s.portfolioUuid, s.tickerName),
			);
			const selectedKeySet = new Set(selectedKeys);

			// With an all-zero selection there is no proportional basis, but the shared `normalise`
			// still fills an UNDERSHOOT by distributing the missing weight equally across the selected
			// leaves — exactly what a freshly-added (weight 0) instrument needs to absorb the gap up to
			// 100 %. Only an OVERSHOOT is genuinely unfixable by a zero-weight selection (nothing to
			// shrink), so bail out (returning the same reference) only in that case.
			const totalEff = allocation.reduce((sum, w) => sum.plus(w), BigNumber(0));
			const selectedEff = selectedKeys.reduce((sum, k) => sum.plus(allocation.get(k) ?? 0), BigNumber(0));
			if (selectedEff.isZero() && totalEff.gte(100)) {
				return composition;
			}

			const normalized = normalise({
				distributeTo: selectedKeys,
				allocation,
				decimalPlaces: Math.max(weightPrecision, weightTotalPrecision + 1),
			});

			const updated = composition.map((c) => {
				if (c.type === "instrument") {
					if (!selectedKeySet.has(directKey(c.tickerName))) {
						return c;
					}
					return { ...c, weight: normalized.get(directKey(c.tickerName))?.toNumber() ?? c.weight };
				}

				// Only rebuild portfolios that own a selected leaf; others keep their weights untouched.
				if (!c.instruments.some((i) => selectedKeySet.has(nestedKey(c.uuid, i.tickerName)))) {
					return c;
				}
				// Inner weights are effective; the outer weight is their sum.
				const instruments = c.instruments.map((i) => ({
					...i,
					weight: normalized.get(nestedKey(c.uuid, i.tickerName))?.toNumber() ?? i.weight,
				}));
				return { ...c, weight: labSum(instruments, (i) => i.weight), instruments };
			});
			return reconcileMonetaryWeights(updated, aum);
		},
	};
}
